import { Component, Prop, Element, h } from '@stencil/core';
import { b2bCalendar } from '../../models/stores/b2bCalendar';
import { b2bCalendarDay } from '../../models/stores/b2bCalendarDay';
import { b2bCalendarTimeInterval } from '../../models/stores/b2bCalendarTimeInterval';
import { ComponentColors, initColors } from '../../utils/constants/colors';
import { z } from 'zod';
import { translationService } from '../../utils/services/translationService';


export const Appearance = z.enum(['default', 'compact']);
export type Appearance = z.infer<typeof Appearance>;

export const ASL_HoursDisplayProps = z.object({
  isExpandable: z.boolean().optional(),
  isExpanded: z.boolean().optional(),
  appearance: Appearance.optional(),
  componentColors: ComponentColors.optional(),
});
export type ASL_HoursDisplayProps = z.infer<typeof ASL_HoursDisplayProps>;

@Component({
  tag: 'aico-store-locator-hours-display',
  styleUrl: 'aico-store-locator-hours-display.css',
  shadow: true
})
export class AicoStoreLocatorHoursDisplay {
  private daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  @Prop() languageKey?: string;
  @Prop() language?: string;


  @Prop() calendar?: b2bCalendar;
  @Prop() isExpandable: boolean = false;
  @Prop({ mutable: true }) isExpanded: boolean = !this.isExpandable;
  @Prop() appearance: Appearance = 'default';

  @Prop() componentColors?: ComponentColors;
  @Element() el!: HTMLElement;

  @Prop() props?: ASL_HoursDisplayProps;

  async componentWillLoad() {
    if (this.props) {
      this.props = ASL_HoursDisplayProps.parse(this.props);
      this.isExpandable = this.props.isExpandable ?? this.isExpandable;
      this.isExpanded = this.props.isExpanded ?? this.isExpanded;
      this.appearance = this.props.appearance ?? this.appearance;
      this.componentColors = this.props.componentColors ?? this.componentColors;
    }

    await translationService.initComponentLoad('aico-store-locator-hours-display');

    if (this.componentColors) {
      this.componentColors = ComponentColors.parse(this.componentColors);
      initColors(this.el, this.componentColors);
    }
  }

  private t(key: string, locale?: string): string {
    return translationService.getTranslation('aico-store-locator-hours-display', key, locale);
  }

  private getCurrentDay(): string {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[new Date().getDay()];
  }

  private formatTime(time: string): string {
    return time.substring(0, 5);
  }

  private createUnknownDay(day: string): b2bCalendarDay {
    return b2bCalendarDay.parse({
      id: -1,
      day,
      isActive: false,
      b2bCalendarTimeIntervals: null
    });
  }

  private getCompleteWeekData(): b2bCalendarDay[] {
    const existingDays = new Set(this.calendar?.b2bCalendarDays?.map(day => day.day) || []);
    return this.daysOfWeek.map(day =>
      existingDays.has(day)
        ? (this.calendar?.b2bCalendarDays!.find(d => d.day === day) || this.createUnknownDay(day))
        : this.createUnknownDay(day)
    );
  }

  private toggleExpanded = () => {
    if (this.isExpandable) {
      this.isExpanded = !this.isExpanded;
    }
  }

  private renderIntervals(intervals: b2bCalendarTimeInterval[] | null) {
    if (!intervals || intervals.length === 0) return null;

    return intervals.map((interval, idx) => (
      <span key={idx} class="block whitespace-nowrap">
        {this.formatTime(interval.startTime)} - {this.formatTime(interval.endTime)}
      </span>
    ));
  }

  private getAppearanceClasses(){
    switch (this.appearance) {
      case 'compact':
        return 'px-2 py-1';
      default:
        return 'p-2';
    }
  }

  private renderDay(day: b2bCalendarDay) {
    const isCurrentDay = day.day === this.getCurrentDay();

    return (
      <div class={`
        flex items-center justify-between
        ${this.getAppearanceClasses()}
        ${isCurrentDay ? 'bg-[--highlight-background-color] rounded-xl' : 'text-[--secondary-text-color]'}
      `}>
        <div class="text-left">
          {this.t(day.day)}
        </div>
        <div class="text-right">
          {!day.isActive ? this.t('Closed') : this.renderIntervals(day.b2bCalendarTimeIntervals)}
        </div>
      </div>
    );
  }

  render() {
    if (!this.calendar || !this.calendar.b2bCalendarDays || this.calendar.b2bCalendarDays.length === 0) {
      return null;
    }
    const completeWeekData = this.getCompleteWeekData();
    const currentDay = completeWeekData.find(day => day.day === this.getCurrentDay());

    return (
      <div>
        {this.isExpandable && (
          <div
            class="flex justify-between items-center mb-2 cursor-pointer"
            onClick={this.toggleExpanded}
          >
            <span class="font-medium">{this.t('Opening hours')}</span>
            <svg
              class={`w-5 h-5 transform transition-transform duration-300
                bg-[--background-color]
                ${this.isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        )}

        <div class="relative bg-[--background-color] rounded-2xl overflow-hidden">
          <div class="p-2">
            {/* Full week view */}
            <div class={`
              transition-all duration-500 ease-in-out
              ${this.isExpanded
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 -translate-y-4 absolute top-0 left-0 right-0'
              }
            `}>
              {completeWeekData.map(day => this.renderDay(day))}
            </div>

            {/* Single day view */}
            {currentDay && (
              <div class={`
                transition-all duration-500 ease-in-out
                ${this.isExpanded
                  ? 'opacity-0 translate-y-4 absolute top-0 left-0 right-0'
                  : 'opacity-100 translate-y-0'
                }
              `}>
                {this.renderDay(currentDay)}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}
